import * as turf from '@turf/turf';
import { Geometry } from '@/graphql/types/geometry';

export type Bbox = [number, number, number, number];

export function filterPolygonsByBbox(polygons: PolygonData[], bbox: Bbox): PolygonData[] {
  const bboxFeature = turf.bboxPolygon(bbox);
  return polygons.filter((polygon) => {
    if (!polygon.geometry?.coordinates) return false;
    const feature = turf.multiPolygon(polygon.geometry.coordinates);
    return turf.booleanIntersects(bboxFeature, feature);
  });
}

interface PolygonData {
  municipality_code: string;
  geometry: Geometry;
}

interface PolygonDatabase {
  checkDataExists: () => Promise<boolean>;
  getAllPolygons: () => Promise<PolygonData[]>;
  storePolygons: (polygons: PolygonData[]) => Promise<void>;
  getPolygonByCode: (municipalityCode: string) => Promise<PolygonData | undefined>;
  getPolygonsByCodes: (municipalityCodes: string[]) => Promise<Map<string, PolygonData>>;
  getPolygonsInBbox: (bbox: Bbox) => Promise<PolygonData[]>;
  clearDatabase: () => Promise<void>;
}

const DB_NAME = 'trri_polygons_db';
const DB_VERSION = 1;
const STORE_NAME = 'brazil_polygons';

class PolygonDatabaseService implements PolygonDatabase {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private openDatabase(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: 'municipality_code',
          });
          objectStore.createIndex('municipality_code', 'municipality_code', {
            unique: true,
          });
        }
      };
    });

    return this.dbPromise;
  }

  async checkDataExists(): Promise<boolean> {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const countRequest = objectStore.count();

      return new Promise((resolve, reject) => {
        countRequest.onsuccess = () => {
          resolve(countRequest.result > 0);
        };

        countRequest.onerror = () => {
          reject(new Error('Failed to check data existence'));
        };
      });
    } catch (error) {
      return false;
    }
  }

  async getAllPolygons(): Promise<PolygonData[]> {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const getAllRequest = objectStore.getAll();

      return new Promise((resolve, reject) => {
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result);
        };

        getAllRequest.onerror = () => {
          reject(new Error('Failed to get all polygons'));
        };
      });
    } catch (error) {
      return [];
    }
  }

  async storePolygons(polygons: PolygonData[]): Promise<void> {
    const db = await this.openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);

    polygons.forEach((polygon) => {
      objectStore.put(polygon);
    });

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        resolve();
      };

      transaction.onerror = () => {
        reject(new Error('Failed to store polygons'));
      };
    });
  }

  async getPolygonByCode(municipalityCode: string): Promise<PolygonData | undefined> {
    try {
      const db = await this.openDatabase();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);
      const getRequest = objectStore.get(municipalityCode);

      return new Promise((resolve, reject) => {
        getRequest.onsuccess = () => {
          resolve(getRequest.result);
        };

        getRequest.onerror = () => {
          reject(new Error('Failed to get polygon by code'));
        };
      });
    } catch (error) {
      return undefined;
    }
  }

  async getPolygonsByCodes(municipalityCodes: string[]): Promise<Map<string, PolygonData>> {
    const result = new Map<string, PolygonData>();
    if (municipalityCodes.length === 0) return result;

    try {
      const db = await this.openDatabase();
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        let pending = municipalityCodes.length;

        transaction.onerror = () => {
          reject(new Error('Failed to get polygons by codes'));
        };

        municipalityCodes.forEach((code) => {
          const request = objectStore.get(code);
          request.onsuccess = () => {
            if (request.result) {
              result.set(code, request.result);
            }
            pending--;
            if (pending === 0) resolve(result);
          };
        });
      });
    } catch (error) {
      return result;
    }
  }

  async getPolygonsInBbox(bbox: Bbox): Promise<PolygonData[]> {
    try {
      const all = await this.getAllPolygons();
      return filterPolygonsByBbox(all, bbox);
    } catch {
      return [];
    }
  }

  async clearDatabase(): Promise<void> {
    const db = await this.openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const objectStore = transaction.objectStore(STORE_NAME);
    const clearRequest = objectStore.clear();

    return new Promise((resolve, reject) => {
      clearRequest.onsuccess = () => {
        resolve();
      };

      clearRequest.onerror = () => {
        reject(new Error('Failed to clear database'));
      };
    });
  }
}

export const polygonDatabaseService = new PolygonDatabaseService();
export type { PolygonData, PolygonDatabase };
